import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { MatIconModule } from '@angular/material/icon';

import { StatComparisonBlock } from '@core/services/ai/ai-assistant.service';

@Component({
  selector: 'app-ai-stat-comparison-block',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule],
  template: `
    @if (block.title) {
      <div class="sc__title">{{ block.title }}</div>
    }
    <div class="sc">
      <div class="sc__side" [attr.data-tone]="block.left.tone || 'neutral'">
        @if (block.left.icon) {
          <mat-icon class="sc__icon">{{ block.left.icon }}</mat-icon>
        }
        <div class="sc__label">{{ block.left.label }}</div>
        <div class="sc__value">{{ block.left.value }}</div>
        @if (block.left.sub) {
          <div class="sc__sub">{{ block.left.sub }}</div>
        }
      </div>
      <div class="sc__sep">
        <span>{{ block.separator || '→' }}</span>
      </div>
      <div class="sc__side" [attr.data-tone]="block.right.tone || 'neutral'">
        @if (block.right.icon) {
          <mat-icon class="sc__icon">{{ block.right.icon }}</mat-icon>
        }
        <div class="sc__label">{{ block.right.label }}</div>
        <div class="sc__value">{{ block.right.value }}</div>
        @if (block.right.sub) {
          <div class="sc__sub">{{ block.right.sub }}</div>
        }
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .sc__title {
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: rgba(15, 23, 42, 0.55);
        margin: 0 0 8px;
      }
      .sc {
        display: grid;
        grid-template-columns: 1fr auto 1fr;
        align-items: stretch;
        gap: 0;
        background: #fff;
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-radius: 12px;
        overflow: hidden;
      }
      .sc__side {
        padding: 14px 16px;
        display: flex;
        flex-direction: column;
        gap: 4px;
        align-items: flex-start;
        border-left: 3px solid rgba(15, 23, 42, 0.1);
        min-width: 0;
      }
      .sc__side:first-child {
        border-left: none;
      }
      .sc__side[data-tone='positive'] {
        border-left-color: #10b981;
        background: rgba(16, 185, 129, 0.04);
      }
      .sc__side[data-tone='warning'] {
        border-left-color: #f59e0b;
        background: rgba(245, 158, 11, 0.05);
      }
      .sc__side[data-tone='danger'] {
        border-left-color: #ef4444;
        background: rgba(239, 68, 68, 0.04);
      }
      .sc__icon {
        font-size: 20px;
        width: 20px;
        height: 20px;
        color: #6366f1;
      }
      .sc__side[data-tone='positive'] .sc__icon {
        color: #10b981;
      }
      .sc__side[data-tone='warning'] .sc__icon {
        color: #f59e0b;
      }
      .sc__side[data-tone='danger'] .sc__icon {
        color: #ef4444;
      }
      .sc__label {
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: rgba(15, 23, 42, 0.55);
        overflow-wrap: anywhere;
      }
      .sc__value {
        font-size: 22px;
        font-weight: 700;
        line-height: 1.1;
        color: rgba(15, 23, 42, 0.95);
        overflow-wrap: anywhere;
      }
      .sc__sub {
        font-size: 12px;
        color: rgba(15, 23, 42, 0.55);
        overflow-wrap: anywhere;
      }
      .sc__sep {
        display: grid;
        place-items: center;
        padding: 0 8px;
        color: rgba(15, 23, 42, 0.4);
        font-size: 18px;
        font-weight: 700;
        background: rgba(15, 23, 42, 0.02);
      }
      @media (max-width: 480px) {
        .sc {
          grid-template-columns: 1fr;
        }
        .sc__sep {
          padding: 4px 0;
          transform: rotate(90deg);
        }
        .sc__side {
          border-left: none;
          border-top: 3px solid rgba(15, 23, 42, 0.1);
        }
        .sc__side:first-child {
          border-top: none;
        }
      }
    `
  ]
})
export class AiStatComparisonBlockComponent {
  @Input({ required: true }) block!: StatComparisonBlock;
}
